import { Component, OnDestroy } from '@angular/core';
import { AppState } from '../../../state/app-state';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { User } from 'viewbid-shared-lib';
import { ProfileService, ProfileStatus } from '../../services/profile.service';
import { selectUser } from 'src/app/viewbid/state/app-selectors';
import {
    Observable,
    Subject,
    filter,
    of,
    switchMap,
    take,
    takeUntil,
} from 'rxjs';
import {
    POST_LOGIN_RETURN_URL_KEY,
    isSafeReturnUrl,
} from '../../utils/auth-return-url.util';

@Component({
    selector: 'app-post-login-page',
    templateUrl: './post-login-page.component.html',
    styleUrls: ['./post-login-page.component.scss'],
})
export class PostLoginPageComponent implements OnDestroy {
    private destroy$ = new Subject<void>();
    private readonly returnUrl: string | null = this.consumeReturnUrl();

    constructor(
        private store: Store<AppState>,
        private router: Router,
        private profileService: ProfileService,
    ) {
        this.profileService.subscribeToUserChanges();

        this.store
            .select<User | null>(selectUser)
            .pipe(
                filter((user: User | null): user is User => !!user),
                take(1),
                switchMap((user: User) => this.profileStatus(user)),
                takeUntil(this.destroy$),
            )
            .subscribe((status: ProfileStatus) => {
                if (status === ProfileStatus.Incomplete) {
                    this.router.navigateByUrl('/profiles/create-profile', {
                        state: {
                            from: 'post-login-page',
                        },
                    });
                } else {
                    this.router.navigateByUrl(
                        isSafeReturnUrl(this.returnUrl)
                            ? this.returnUrl
                            : '/home',
                    );
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private profileStatus(user: User): Observable<ProfileStatus> {
        return user.accountMappingsId
            ? of(ProfileStatus.Complete)
            : this.profileService.resolveProfileStatus();
    }

    /** Reads and clears the post-login return URL captured at /login, so it's
     *  used once and can't leak into a later login. */
    private consumeReturnUrl(): string | null {
        const url: string | null = sessionStorage.getItem(
            POST_LOGIN_RETURN_URL_KEY,
        );
        sessionStorage.removeItem(POST_LOGIN_RETURN_URL_KEY);
        return url;
    }
}
